import { useEffect, useState } from 'react';
import { getBlob } from '../api/client';

interface UserAvatarProps {
  userId: number | null | undefined;
  /** Hint from /me — skip the fetch when no avatar is set. */
  hasAvatar?: boolean;
  alt?: string;
  className?: string;
  /** Bump to force a re-fetch after a replace (avatar_path may be unchanged
   *  when the vault swaps the file). */
  refreshKey?: number | string;
  /** Rendered while loading, when there's no avatar, or if the fetch fails. */
  fallback?: JSX.Element | null;
}

/** User avatar image. GET /api/users/:id/avatar is Bearer-auth only (see
 *  parseToken — no cookie fallback), so a bare <img src="/api/users/:id/avatar">
 *  can't send the auth header: it 401s and renders as a broken image. Like
 *  <PersonAvatar>, we fetch the bytes with the auth header and render an
 *  object URL instead. */
export function UserAvatar({
  userId,
  hasAvatar,
  alt = '',
  className,
  refreshKey,
  fallback = null,
}: UserAvatarProps): JSX.Element | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    setUrl(null);
    if (!userId || hasAvatar === false) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    void (async () => {
      try {
        const blob = await getBlob(`/api/users/${userId}/avatar`);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        /* 404 / no avatar → fall back */
      }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [userId, hasAvatar, refreshKey]);

  if (url) return <img src={url} alt={alt} className={className} />;
  return fallback;
}
